/**
 * Displays second level report details in a modal window.
 */

import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import { errorParser } from "../../utils";

import ReportModel from "../../models/report";
import Body from "./body";
import FooterActionsView from "../generic/footer-actions";
import ButtonCollectionView from "../generic/button/collection";
import buttonData from "../../config/report-buttons/drilldown";

const channel = Radio.channel("global");

const DEFAULTS = {
  el: "#shared-modal",
};

const MODAL_OPTIONS = {
  // removes the ability to dismiss model via escape key
  keyboard: false,
  // removes the ability to dismiss model by clicking on modal backdrop
  backdrop: "static",
};

const CLASS_NAME = "no-scroll";

class View extends Marionette.LayoutView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  template() {
    return false;
  }

  regions() {
    return {
      titleRegion: ".modal-title",
      bodyRegion: "div.modal-body",
      footerActions: "div.modal-footer div.actions",
      footerFormButtons: "div.modal-footer div.form-buttons",
    };
  }

  childEvents() {
    return {
      // title can be changed by the body view
      "update:title": "_onTitleUpdated",
    };
  }

  initialize() {
    this.modalAPI = null;

    // localize endpoint to use as the default tab indicator by body view
    this.activeTabEndpoint = this.model.getEndpoint();

    // add custom class names
    this.$el.addClass(CLASS_NAME);

    // extend report model with URL root
    const Model = ReportModel.extend({
      urlRoot: this.model.getUrl(),
    });

    // instantiate model and listen for changes once
    this.viewModel = new Model();
    this.listenToOnce(this.viewModel, "change", this._onViewModelChange);

    // use a promise to track data fetching
    const promise = this.viewModel.fetch();
    promise.fail((jqXHR) => {
      // display error message
      channel.trigger("status:show", {
        autoDismiss: false,
        type: "danger",
        message: errorParser(jqXHR),
      });
    });
  }

  _onViewModelChange(model) {
    const meta = model.getMeta();

    // make sure this drilldown has a menu configuration
    const menuItems = meta.has("menu") ? meta.getMenu() : null;
    if (menuItems) {
      // find active tab by endpoint URL
      const tab = menuItems.findWhere({ url: this.activeTabEndpoint });
      if (tab) {
        tab.setActive(true);

        // extract selected params from parent report
        const data = meta.getParamsSelected() || {};

        // replace default filters with the selected params
        tab.setData(data);
      }

      try {
        // add menu items to router
        channel.request("router:add", menuItems);
      } catch (e) {
        console.warn("Unable to add dynamic routes");
        throw e;
      }

      this._setContent(model);
    }
  }

  _onTitleUpdated(view, title) {
    // set title
    this.titleRegion.$el.html(title);
  }

  _setContent(model) {
    // make modal dialog large
    this.$("div.modal-dialog").addClass("modal-lg");

    // set title
    const meta = model.get("meta");
    const title = meta.has("menuTitle")
      ? meta.getMenuTitle().getDisplay()
      : null;
    if (title) {
      this.titleRegion.$el.html(title);
    } else {
      console.warn("menuTitle was not found");
    }

    // create content
    this.bodyRegion.show(
      new Body({
        // pull layout from parent model
        type: this.model.get("layout"),
        // meta model
        model: model.getMeta(),
      })
    );

    // create button collection views
    this.footerActions.show(new FooterActionsView());

    this.footerFormButtons.show(
      new ButtonCollectionView({
        className: "pull-right",
        buttons: buttonData,
      })
    );

    // Instantiate the bootstrap modal, which will show the component.
    this.modalAPI = this.$el

      // handle model events
      // http://getbootstrap.com/javascript/#modals-events
      .on("show.bs.modal", this._onShow.bind(this))
      .on("shown.bs.modal", this._onShown.bind(this))
      .on("hide.bs.modal", this._onHide.bind(this))
      .on("hidden.bs.modal", this._onHidden.bind(this))

      // instantiate bootstrap modal
      .modal(MODAL_OPTIONS);
  }

  unsetContent() {
    // remove custom class names
    this.$el.removeClass(CLASS_NAME);
    // remove large modal classname
    this.$("div.modal-dialog").removeClass("modal-lg");

    this.$(".modal-title").html("");
    this.titleRegion.empty();
    this.bodyRegion.empty();
    this.footerActions.empty();
    this.footerFormButtons.empty();
  }

  _onShow() {
    // no-op
  }

  _onShown() {
    // no-op
  }

  _onHide() {
    // no-op
  }

  _onHidden() {
    // remove listeners
    this.modalAPI.off();

    // remove content
    this.unsetContent();

    // redirect
    if (this.options.returnRoute) {
      if ("back" === this.options.returnRoute) {
        // request previous route
        channel.request("router:previousRoute", {
          reload: !!this.options.returnRouteRefresh,
        });
      } else {
        // request configured route
        channel.request("router:returnToRoute", this.options.returnRoute, {
          reload: !!this.options.returnRouteRefresh,
        });
        // also triggers a tab activation event
        channel.trigger("tab-activated", this.options.returnRoute);
      }
    } else {
      // redirect home
      channel.request("router:defaultRoute", {
        reload: !!this.options.returnRouteRefresh,
      });
    }
  }

  // dismisses modal by calling its API
  hide() {
    try {
      this.modalAPI.modal("hide");
    } catch (e) {
      console.warn("Unable to dismiss modal", this);
    }
  }
}

export { View as default };
